/**
 * Dashboard — Collections widget.
 *
 * Three mini cards (Collected/Partial/Overdue) + a 5-row "Recent
 * collections" table + a "Record payment" modal that calls the existing
 * POST /api/trips/:tripId/receipts endpoint. An amber banner surfaces
 * past-due outstanding so collections never fall off the operator's radar.
 *
 * Data source: aggregated server-side at GET /api/dashboard/collections
 * (mirrors the same trip.receipts logic the rest of the app uses, so the
 * number on this widget always matches the Trip Finance tab).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Wallet, AlertTriangle, Plus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtINR = (n) => `₹${Math.round(Number(n || 0)).toLocaleString('en-IN')}`;
const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }); }
  catch { return s; }
};

const CollectionsWidget = () => {
  const [data, setData] = useState({ summary: { collected: 0, partial: 0, overdue: 0 }, recent: [], overdueTripsCount: 0, overdueTotal: 0 });
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/dashboard/collections`, { withCredentials: true });
      setData(res.data);
    } catch {
      toast.error('Failed to load collections');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  return (
    <Card className="p-5 border-slate-200" data-testid="collections-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Wallet className="h-5 w-5 text-emerald-600" />
          <h3 className="text-base font-semibold text-slate-900">Bank Collections</h3>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/finance/receipts"
            data-testid="collections-view-all"
            className="text-[11px] text-teal-700 hover:text-teal-800 hover:underline"
          >
            View all →
          </Link>
          <Button onClick={() => setModalOpen(true)} className="bg-emerald-700 hover:bg-emerald-800 text-white" size="sm" data-testid="record-payment-btn">
            <Plus className="h-4 w-4 mr-1" /> Record payment
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4" data-testid="collections-summary">
        <MiniCard label="Collected" value={fmtINR(data.summary.collected)} tone="emerald" />
        <MiniCard label="Partial" value={fmtINR(data.summary.partial)} tone="amber" />
        <MiniCard label="Overdue" value={fmtINR(data.summary.overdue)} tone="rose" />
      </div>

      {data.overdueTripsCount > 0 ? (
        <Link
          to="/quotes?status=overdue&from=collections"
          className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 hover:bg-amber-100 px-3 py-2 rounded-lg mb-3 transition-colors"
          data-testid="overdue-alert"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span className="flex-1">
            {data.overdueTripsCount} trip{data.overdueTripsCount === 1 ? '' : 's'} have overdue collections — {fmtINR(data.overdueTotal)} total pending.
          </span>
          <span className="font-semibold underline-offset-2 underline">View overdue</span>
        </Link>
      ) : null}

      <RecentCollectionsTable rows={data.recent} loading={loading} />

      <RecordPaymentModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        onSaved={() => { setModalOpen(false); fetchData(); }}
      />
    </Card>
  );
};

const MiniCard = ({ label, value, tone }) => {
  const colorBg = tone === 'emerald' ? 'bg-emerald-50' : tone === 'amber' ? 'bg-amber-50' : 'bg-rose-50';
  const colorText = tone === 'emerald' ? 'text-emerald-700' : tone === 'amber' ? 'text-amber-700' : 'text-rose-700';
  return (
    <div className={`${colorBg} rounded-lg p-3`} data-testid={`mini-card-${tone}`}>
      <div className={`text-[10px] font-semibold uppercase tracking-wider ${colorText}`}>{label}</div>
      <div className={`text-base font-mono font-semibold mt-0.5 ${colorText}`}>{value}</div>
    </div>
  );
};

const RecentCollectionsTable = ({ rows, loading }) => {
  if (loading) return <div className="py-6 text-center text-xs text-slate-400">Loading…</div>;
  if (!rows || rows.length === 0) return <div className="py-6 text-center text-xs text-slate-400">No recent collections.</div>;
  return (
    <div className="overflow-x-auto" data-testid="recent-collections-table">
      <table className="w-full text-xs">
        <thead className="text-slate-500 font-semibold uppercase tracking-wider text-[10px]">
          <tr className="border-b border-slate-100">
            <th className="text-left py-2 px-2">Trip ID</th>
            <th className="text-left py-2 px-2">Traveller</th>
            <th className="text-left py-2 px-2">Type</th>
            <th className="text-right py-2 px-2">Amount</th>
            <th className="text-right py-2 px-2">Date</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {rows.map((r, i) => (
            <CollectionRow key={i} r={r} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

const CollectionRow = ({ r }) => {
  const typeColor = r.type === 'Full' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700';
  return (
    <tr data-testid={`collection-row-${r.tripId}`} className="hover:bg-slate-50">
      <td className="py-2 px-2 font-mono">{r.displayTripId || r.tripId}</td>
      <td className="py-2 px-2">{r.traveller}</td>
      <td className="py-2 px-2">
        <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${typeColor}`}>{r.type}</span>
      </td>
      <td className="py-2 px-2 text-right font-mono font-semibold">{fmtINR(r.amount)}</td>
      <td className="py-2 px-2 text-right font-mono text-slate-500">{fmtDate(r.date)}</td>
    </tr>
  );
};

// ---------------------------------------------------------------------------
// Record-payment modal
// ---------------------------------------------------------------------------

const RecordPaymentModal = ({ open, onOpenChange, onSaved }) => {
  const [tripQuery, setTripQuery] = useState('');
  const [tripOptions, setTripOptions] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [amount, setAmount] = useState('');
  const [paymentType, setPaymentType] = useState('Partial');
  const [method, setMethod] = useState('Bank Transfer');
  const [reference, setReference] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  // Trip search — fires after 300 ms of typing.
  useEffect(() => {
    if (!open) return;
    if (!tripQuery || tripQuery.length < 2) { setTripOptions([]); return; }
    const id = setTimeout(async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/trips?search=${encodeURIComponent(tripQuery)}&limit=8`, { withCredentials: true });
        const items = Array.isArray(res.data) ? res.data : (res.data?.items || []);
        setTripOptions(items.slice(0, 8));
      } catch { /* silent */ }
    }, 300);
    return () => clearTimeout(id);
  }, [tripQuery, open]);

  const reset = () => {
    setTripQuery(''); setTripOptions([]); setSelectedTrip(null);
    setAmount(''); setPaymentType('Partial'); setMethod('Bank Transfer');
    setReference(''); setDate(new Date().toISOString().slice(0, 10)); setNote('');
  };

  const save = async () => {
    if (!selectedTrip) { toast.error('Select a trip'); return; }
    if (!amount || Number(amount) <= 0) { toast.error('Enter a valid amount'); return; }
    setBusy(true);
    try {
      await axios.post(`${BACKEND_URL}/api/trips/${selectedTrip.tripId}/receipts`, {
        date,
        amount: Number(amount),
        method,
        bankRef: reference,
        note,
      }, { withCredentials: true });
      toast.success('Payment recorded');
      reset();
      onSaved();
    } catch (err) {
      const detail = err && err.response && err.response.data && err.response.data.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to record payment');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-lg" data-testid="record-payment-modal">
        <DialogHeader>
          <DialogTitle>Record payment</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label>Trip</Label>
            {selectedTrip ? (
              <div className="flex items-center justify-between gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mt-1" data-testid="selected-trip">
                <div className="text-sm">
                  <span className="font-mono font-semibold">{selectedTrip.displayTripId || selectedTrip.tripId}</span> · {selectedTrip.clientName} · {selectedTrip.destination}
                </div>
                <button type="button" onClick={() => setSelectedTrip(null)} className="text-xs text-emerald-700 font-semibold">Change</button>
              </div>
            ) : (
              <div className="relative mt-1">
                <Input
                  placeholder="Type trip ID or traveller name to search…"
                  value={tripQuery}
                  onChange={(e) => setTripQuery(e.target.value)}
                  data-testid="trip-search-input"
                />
                {tripOptions.length > 0 ? (
                  <div className="absolute left-0 right-0 top-full mt-1 max-h-56 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg z-50">
                    {tripOptions.map((t) => (
                      <button
                        type="button"
                        key={t.tripId}
                        onClick={() => { setSelectedTrip(t); setTripOptions([]); setTripQuery(''); }}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 flex items-center justify-between"
                        data-testid={`trip-option-${t.tripId}`}
                      >
                        <span>
                          <span className="font-mono font-semibold">{t.displayTripId || t.tripId}</span> · {t.clientName}
                        </span>
                        <span className="text-slate-400">{t.destination}</span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Amount (₹)</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="amount-input" />
            </div>
            <div>
              <Label>Payment type</Label>
              <Select value={paymentType} onValueChange={setPaymentType}>
                <SelectTrigger data-testid="payment-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Full">Full</SelectItem>
                  <SelectItem value="Partial">Partial</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                  <SelectItem value="UPI">UPI</SelectItem>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Cheque">Cheque</SelectItem>
                  <SelectItem value="Card">Card</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date of receipt</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="date-input" />
            </div>
          </div>
          <div>
            <Label>UTR / Reference (optional)</Label>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} data-testid="reference-input" />
          </div>
          <div>
            <Label>Note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="note-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="save-payment-btn">
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CollectionsWidget;
